import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Injectable,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewEncapsulation,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import {
    enumMapper,
    IRobot,
    ISession,
    ISessionUpdate,
    LicenseErrorCode,
    ODataResponse,
    ROBOT_STATE_FILTER,
    RobotHostingType,
    RobotState,
} from '@models';
import { FILTER_BUILDER } from '@pages/generics/filter-builder';
import {
    GRID_SERVICE,
    IGridSource,
    ODataGridComponent,
} from '@pages/generics/grid';
import { RobotFilterBuilder } from '@pages/robots/filter-builder/robot-filter-builder';
import {
    ODataQueryOptions,
    SessionsService,
    SignalRService,
} from '@services';

import { of } from 'rxjs';
import {
    map,
    takeUntil,
} from 'rxjs/operators';

export type IRobotState = IRobot & Partial<ISession> & { sessionId: number };

// since we get the selected Robot Ids from the server, we'll overwrite the sessionId
const mapRobotIdToSessionId = (source: ODataResponse<ISession>) => ({
    total: source.total,
    data: source.data.map(e =>
        ({
            ...e,
            sessionId: e.id,
            id: e.robot.id,
        }),
    ),
}) as ODataResponse<IRobotState>;

@Injectable()
export class ProcessRobotsSource implements IGridSource<ODataQueryOptions, ODataResponse<IRobotState>> {
    public environmentId?: number;

    public get mapper() {
        return this._sessionService.mapper;
    }

    constructor(
        private _sessionService: SessionsService,
    ) { }

    public get(qo: ODataQueryOptions) {
        return !this.environmentId ?
            of({ data: [], total: 0 }) :
            this._sessionService
                .getSessionsForEnvironment(this.environmentId, qo)
                .pipe(
                    map(mapRobotIdToSessionId),
                );
    }
}

@Component({
    selector: 'ui-process-robots-grid',
    templateUrl: './process-robots.grid.html',
    styleUrls: ['./process-robots.grid.scss'],
    providers: [
        SessionsService,
        {
            provide: GRID_SERVICE,
            useClass: ProcessRobotsSource,
        },
        {
            provide: FILTER_BUILDER,
            useClass: RobotFilterBuilder,
        },
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
})
export class ProcessRobotsGridComponent
    extends ODataGridComponent<IRobotState, number>
    implements OnInit, OnDestroy {
    public RobotHostingType = RobotHostingType;
    public LicenseErrorCode = LicenseErrorCode;

    @Input()
    public virtualScroll = false;

    @Input()
    public set environmentId(value: number) {
        const svc = this._service as unknown as ProcessRobotsSource;
        svc.environmentId = value;
        this.clearSelection();
        this._refresh$.next();
    }

    @Input()
    public isJobsGrid = true;

    @Output()
    public selectionChange = new EventEmitter<number>();

    @Input()
    public set selectionFormControl(selectionFormControl: FormControl) {
        this._selectionFormControl = selectionFormControl;
        this.selectedRobots = selectionFormControl.value;
    }
    public get selectionFormControl() {
        return this._selectionFormControl!;
    }

    public get selectedRobots(): Array<IRobotState> {
        return this._grid.selectionManager.selected || [];
    }

    public set selectedRobots(robots: Array<IRobotState>) {
        this._grid.selectionManager.select(...robots);
    }

    public stateList = enumMapper(RobotState, {
        resourceKey: 'RobotState',
        filterValueMap: ROBOT_STATE_FILTER,
    });

    private _signalRService = this._injector.get<SignalRService>(SignalRService);

    private _selectionFormControl?: FormControl;

    ngOnInit() {
        this._grid
            .selectionManager
            .changed$
            .pipe(takeUntil(this._destroyed$))
            .subscribe(() => {
                this.selectionChange.next(this._grid.selectionManager.selected.length);

                if (this._selectionFormControl) {
                    this._selectionFormControl.setValue([
                        ...this._grid.selectionManager.selected,
                    ]);
                    this._selectionFormControl.markAsDirty();
                }
            });

        if (!this.isJobsGrid) { return; }

        this.data$.pipe(
            takeUntil(this._destroyed$),
        ).subscribe((data) =>
            this._signalRService
                .watchRobots(data.map(robot => robot.id)),
        );

        this._signalRService
            .sessions$
            .pipe(takeUntil(this._destroyed$))
            .subscribe(this._updateJobState);
    }

    ngOnDestroy() {
        super.ngOnDestroy();
        this._signalRService.watchRobots(false);
    }

    public clearSelection = () =>
        this._grid.selectionManager.clear()

    private _updateJobState = (update: ISessionUpdate) => {
        const rowToPatch = this._grid.dataManager.data$.value.find(
            robot => robot.sessionId === update.id,
        );
        if (!rowToPatch) { return; }

        this._grid.dataManager
            .patchRow(rowToPatch.id, {
                state: update.state,
            });
    }
}
